import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { EditorValue } from "../../../../../../services/api/instances/editorApi/types";
import { useAppSelector } from "../../../../../../services/redux/hooks/use-selector";
import { AppButton } from "../../../../../../shared/components/AppButton";
import { getPublishSpaVersion } from "../../../../helpers";
import { StorageObjectVersion } from "../../../../../../services/api/instances/storageApi/types/object_types";
import { useStorageSelectors } from "../../../../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { selectSpaValue } from "../../../../redux/selectors";
import { useAppMenus } from "../../../../../../app/state/menuSlice/hook/use-app-menus";
import { PublishVersionSelectorMenu } from "../PublishVersionSelectorMenu";
import { TValueForService } from "../../../../../../services/api/instances/storageApi/types/service_types";
import { getSpaUpdatedValue } from "../../../../helpers/getSpaUpdatedValue";
import styles from "../styles.module.css";

interface Props {
  saveSpa: (value: TValueForService<"ai_agents_spa">) => void;
}

export const LinkShareController = ({ saveSpa }: Props) => {
  const { handleCloseMenu, handleOpenMenu } = useAppMenus();
  const { versions, selectedId } = useStorageSelectors("ai_agents_spa");
  const spaValue = useAppSelector(selectSpaValue);
  const [publicVersion, setPublicVersion] =
    useState<StorageObjectVersion<EditorValue> | null>(null);
  const { t } = useTranslation();

  const spaVersions = useMemo(() => {
    if (!versions || !selectedId) return [];
    return versions[selectedId] as StorageObjectVersion<EditorValue>[];
  }, [versions, selectedId]);

  const handleChangePublishVersion = (
    version: StorageObjectVersion<EditorValue> | string,
  ) => {
    if (!spaValue) return;
    const updatedValue = getSpaUpdatedValue(spaValue, {
      uri_object: {
        ...spaValue?.spa_object?.uri_object,
        publish: {
          ...spaValue?.spa_object?.uri_object?.publish,
          ref:
            typeof version === "string"
              ? ""
              : `${version.ref}:ID_${version.version_id}`,
        },
      },
    });
    saveSpa(updatedValue);
  };

  useEffect(() => {
    if (!spaValue || !selectedId || !versions) return;
    const publicVersion = getPublishSpaVersion(selectedId, versions, spaValue);
    setPublicVersion(publicVersion as StorageObjectVersion<EditorValue>);
  }, [selectedId, versions, handleChangePublishVersion]);

  return (
    <>
      <div className={styles["publish-placeholder"]}>
        <div className={styles["publish-version-info"]}>
          {publicVersion && (
            <div className={styles["publish-button-label"]}>
              {t("editor.publish_label")}:{" "}
            </div>
          )}
          <AppButton
            buttonType={"text"}
            onClick={(e) => handleOpenMenu(e, "publish_version_menu", null)}
            className={styles["publish-button"]}
            disabled={spaVersions?.length === 0}
          >
            {publicVersion ? (
              <div>{publicVersion?.version_name}</div>
            ) : (
              <span>
                {spaVersions?.length === 0
                  ? `${t("editor.no_publish_label")}`
                  : `${t("editor.publish-button")}`}
              </span>
            )}
          </AppButton>
        </div>
      </div>
      <PublishVersionSelectorMenu
        versions={spaVersions}
        closeMenu={handleCloseMenu}
        handleChangePublishVersion={handleChangePublishVersion}
      />
    </>
  );
};
